import PropTypes from 'prop-types';
import { MapPin, Users, Calendar } from 'lucide-react';
import { Heading, Text, Stack } from '@/components/ui/index.js';
import styles from './EventCard.module.css';

const sportIcons = {
  basketball: '🏀',
  soccer: '⚽',
  tennis: '🎾',
  running: '🏃',
  swimming: '🏊',
  cycling: '🚴',
  yoga: '🧘',
};

/**
 * EventCard - Displays event information with glassmorphism styling
 * 
 * @param {Object} event - Event data
 * @param {string} variant - Card variant: 'featured' | 'compact'
 * @param {function} onClick - Click handler
 * @param {Object} style - Additional inline styles
 */
export function EventCard({ event, variant = 'featured', onClick, style }) {
  const isCompact = variant === 'compact';
  const spotsAvailable = event.maxParticipants - event.participants;
  const isAlmostFull = spotsAvailable <= 2;
  const isFull = spotsAvailable === 0;

  return (
    <article 
      className={`${styles.card} ${styles[variant]} ${isFull ? styles.full : ''}`}
      onClick={onClick}
      style={style}
    >
      {/* Image Placeholder */}
      <div className={styles.imageContainer}>
        <div className={styles.imagePlaceholder}>
          <span className={styles.sportEmoji}>
            {sportIcons[event.sport] || '🏃'}
          </span>
        </div>
        {isAlmostFull && !isFull && (
          <span className={`${styles.badge} ${styles.almostFull}`}>
            Almost Full
          </span>
        )}
        {isFull && (
          <span className={`${styles.badge} ${styles.full}`}>
            Full
          </span>
        )}
      </div>

      {/* Content */}
      <div className={styles.content}>
        <Stack direction="column" gap={isCompact ? 1 : 2}>
          <Heading 
            level={3} 
            size={isCompact ? 'sm' : 'md'} 
            color="primary"
            className={styles.title}
          >
            {event.title}
          </Heading>

          <div className={styles.details}>
            <div className={styles.detailItem}>
              <Calendar size={isCompact ? 14 : 16} className={styles.detailIcon} />
              <Text size={isCompact ? 'xs' : 'sm'} color="secondary">
                {event.date}
              </Text>
            </div>
            
            <div className={styles.detailItem}>
              <MapPin size={isCompact ? 14 : 16} className={styles.detailIcon} />
              <Text size={isCompact ? 'xs' : 'sm'} color="secondary" truncate>
                {event.location}
              </Text>
            </div>

            <div className={styles.detailItem}>
              <Users size={isCompact ? 14 : 16} className={styles.detailIcon} />
              <Text size={isCompact ? 'xs' : 'sm'} color="secondary">
                <span className={isAlmostFull && !isFull ? styles.spotsLow : ''}>
                  {event.participants}/{event.maxParticipants}
                </span>
                {' '}joined
              </Text>
            </div>
          </div>
        </Stack>

        {!isCompact && !isFull && (
          <button 
            className={styles.joinButton}
            onClick={(e) => {
              e.stopPropagation();
              // Handle join action
            }}
          >
            Join
          </button>
        )}

        {!isCompact && isFull && (
          <button 
            className={`${styles.joinButton} ${styles.waitlistButton}`}
            onClick={(e) => {
              e.stopPropagation();
              // Handle waitlist action
            }}
          >
            Waitlist
          </button>
        )}
      </div>
    </article>
  );
}

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    participants: PropTypes.number.isRequired,
    maxParticipants: PropTypes.number.isRequired,
    sport: PropTypes.string.isRequired,
  }).isRequired,
  variant: PropTypes.oneOf(['featured', 'compact']),
  onClick: PropTypes.func,
  style: PropTypes.object,
};

export default EventCard;
